import React from 'react';
import { Dialog, DialogContent, Grid, IconButton, Typography, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import launchLogo from '../../assets/images/logo.png';
import ContactUsForm from '../ContactUsForm/ContactUsForm';
import { launchScreenshots } from '../../constants/launchScreenshots';
import CloseIcon from '@mui/icons-material/Close';
import SEO from '../common/SEO/SEO';
import {
  LaunchWrapper,
  HeroSection,
  HeroTopRow,
  HeroLogo,
  HeroHeadlineBlock,
  HeroBadge,
  HeroBrandTitle,
  HeroTagline,
  HeroSubtitle,
  LaunchDateSection,
  LaunchDateKicker,
  LaunchDateHeading,
  LaunchDateDisplay,
  LaunchDatePart,
  LaunchDatePartLabel,
  LaunchDatePartValue,
  LaunchDateDivider,
  LaunchDateDescription,
  ContactSignupSection,
  ContactSignupDividerRow,
  ContactSignupDividerLine,
  ContactSignupDividerLabel,
  ContactSignupSubtitle,
  ContactSignupButton,
  PreviewSection,
  SectionKicker,
  SectionTitle,
  LaunchPanel,
  LaunchPanelHeader,
  LaunchPanelTitle,
  LaunchPanelSubtitle,
  LaunchPanelBody,
  AboutInnerWell,
  AboutPointsStack,
  AboutPointCard,
  AboutPointMark,
  AboutPointText,
  AboutPointTitle,
  AboutPointDescription,
  RolloutBanner,
  ScreenshotFigure,
  ScreenshotButton,
  ScreenshotImage,
  ScreenshotCaption,
  EmptyScreenshotState,
  LightboxHeader,
  LightboxImage,
} from './LaunchPage.styled';

const aboutPoints = [
  {
    mark: '1',
    title: 'A local marketplace for homeowners & pros',
    description:
      'NKY Connect is built for Northern Kentucky: homeowners who need quality work, and the plumbers, electricians, HVAC techs, landscapers, and other pros who keep neighborhoods running. Listings, reviews, and messaging focus on real projects in Boone, Kenton, Campbell, and nearby communities—not generic national directories.',
  },
  {
    mark: '2',
    title: 'No fee for users — compare pricing with confidence',
    description:
      'There is no fee for homeowners to use NKY Connect. Compare provider pricing, services, and reviews up front so you know what you are getting into before you reach out.',
  },
  {
    mark: '3',
    title: 'Honest storefronts & straightforward requests',
    description:
      'We prioritize clarity: transparent provider profiles and simple ways to request work. Guided flows help you describe the job and surface providers who fit—so getting to a quote feels direct, not confusing.',
  },
  {
    mark: '4',
    title: 'Compare options without marketplace noise',
    description:
      'Tools help you compare providers on your terms, with neighbor-backed reviews and clear service details—instead of wading through national spam lists or endless guesswork.',
  },
];

const providerBenefits = [
  {
    mark: '1',
    title: 'A dedicated storefront page',
    description:
      'Show your services, pricing, business details, and local reviews in one place—so customers can understand your offering before they reach out.',
  },
  {
    mark: '2',
    title: 'Customer inquiries from matched requests',
    description:
      'Get service requests from residents who are actively looking for your type of work. The wizard helps route the right projects to the right providers.',
  },
  {
    mark: '3',
    title: 'Analytics to track performance',
    description:
      'See how customers are finding you and what is converting—so you can make smarter decisions about services, pricing, and availability.',
  },
  {
    mark: '4',
    title: 'Flat subscription — no per-request commission',
    description:
      'Keep your margins. We aim for simple, predictable subscription pricing instead of charging a commission every time a customer submits a service request.',
  },
];

const rolloutMessage =
  'We are rolling out thoughtfully ahead of our July 27 launch—if you are a provider or a homeowner with feedback, use the form below and we will get back to you.';

export default function LaunchPage() {
  const [activeScreenshot, setActiveScreenshot] = React.useState(null);

  const openScreenshot = (screenshot) => setActiveScreenshot(screenshot);
  const closeScreenshot = () => setActiveScreenshot(null);

  return (
    <LaunchWrapper>
      <SEO
        title="Coming soon — NKY Connect"
        description="NKY Connect is launching soon — a local-first platform to find, compare, and connect with trusted home service providers in Northern Kentucky."
        path="/launch"
      />
      <HeroSection>
        <HeroBadge component="span">Official launch — July 27, 2026</HeroBadge>
        <HeroTopRow>
          <HeroLogo src={launchLogo} alt="" />
          <HeroHeadlineBlock>
            <HeroBrandTitle>NKY Connect</HeroBrandTitle>
            <HeroTagline>
              Connecting Northern Kentucky residents to trusted home service providers
            </HeroTagline>
            <HeroSubtitle>
              NKY Connect is a local-first platform designed to make it easier to find,
              compare, and connect with reliable providers in your community.
            </HeroSubtitle>
          </HeroHeadlineBlock>
        </HeroTopRow>
      </HeroSection>

      <LaunchDateSection>
        <LaunchDateKicker component="p">Save the date</LaunchDateKicker>
        <LaunchDateHeading>Our official launch is July 27th</LaunchDateHeading>
        <LaunchDateDisplay aria-label="Official launch date: July 27, 2026">
          <LaunchDatePart>
            <LaunchDatePartLabel>Month</LaunchDatePartLabel>
            <LaunchDatePartValue>July</LaunchDatePartValue>
          </LaunchDatePart>
          <LaunchDateDivider aria-hidden />
          <LaunchDatePart>
            <LaunchDatePartLabel>Day</LaunchDatePartLabel>
            <LaunchDatePartValue>27</LaunchDatePartValue>
          </LaunchDatePart>
          <LaunchDateDivider aria-hidden />
          <LaunchDatePart>
            <LaunchDatePartLabel>Year</LaunchDatePartLabel>
            <LaunchDatePartValue>2026</LaunchDatePartValue>
          </LaunchDatePart>
        </LaunchDateDisplay>
        <LaunchDateDescription>
          On launch day, NKY Connect opens to homeowners and service providers across Northern
          Kentucky. Providers can sign up now to be ready when we go live.
        </LaunchDateDescription>
      </LaunchDateSection>

      <PreviewSection>
        <SectionKicker component="p">Inside the app</SectionKicker>
        <SectionTitle>See how NKY Connect works</SectionTitle>
        <Grid container spacing={2.5}>
          {launchScreenshots.length > 0 ? (
            launchScreenshots.map((screenshot) => (
              <Grid item xs={12} sm={6} md={4} key={screenshot.alt}>
                <ScreenshotFigure>
                  <ScreenshotButton
                    type="button"
                    onClick={() => openScreenshot(screenshot)}
                    aria-label={`Expand screenshot: ${screenshot.alt}`}
                  >
                    <ScreenshotImage src={screenshot.src} alt={screenshot.alt} loading="lazy" />
                    <ScreenshotCaption component="figcaption">{screenshot.caption}</ScreenshotCaption>
                  </ScreenshotButton>
                </ScreenshotFigure>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <EmptyScreenshotState>
                Add PNG files under <code>src/assets/screenshots</code> and list them in{' '}
                <code>src/constants/launchScreenshots.js</code>.
              </EmptyScreenshotState>
            </Grid>
          )}
        </Grid>
      </PreviewSection>

      <Dialog
        open={Boolean(activeScreenshot)}
        onClose={closeScreenshot}
        maxWidth="lg"
        fullWidth
      >
        <LightboxHeader>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', pr: 2 }}>
            {activeScreenshot?.caption || activeScreenshot?.alt || 'Screenshot'}
          </Typography>
          <IconButton onClick={closeScreenshot} aria-label="Close screenshot">
            <CloseIcon />
          </IconButton>
        </LightboxHeader>
        <DialogContent sx={{ pt: 2 }}>
          {activeScreenshot && (
            <LightboxImage src={activeScreenshot.src} alt={activeScreenshot.alt} />
          )}
        </DialogContent>
      </Dialog>

      <Box sx={{ mt: 3 }}>
        <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <LaunchPanel elevation={0}>
            <LaunchPanelHeader>
              <LaunchPanelTitle component="h2" variant="h6">
                About NKY Connect
              </LaunchPanelTitle>
              <LaunchPanelSubtitle>
                Local homeowners deserve a simpler way to hire—and trusted pros deserve to
                be found for the work they do best.
              </LaunchPanelSubtitle>
            </LaunchPanelHeader>
            <LaunchPanelBody>
              <AboutInnerWell>
                <AboutPointsStack>
                  {aboutPoints.map((point) => (
                    <AboutPointCard key={point.title}>
                      <AboutPointMark aria-hidden>{point.mark}</AboutPointMark>
                      <AboutPointText>
                        <AboutPointTitle>{point.title}</AboutPointTitle>
                        <AboutPointDescription>{point.description}</AboutPointDescription>
                      </AboutPointText>
                    </AboutPointCard>
                  ))}
                </AboutPointsStack>
              </AboutInnerWell>
            </LaunchPanelBody>
          </LaunchPanel>
        </Grid>

        <Grid item xs={12} md={6}>
          <LaunchPanel elevation={0}>
            <LaunchPanelHeader>
              <LaunchPanelTitle component="h2" variant="h6">
                Built for local businesses
              </LaunchPanelTitle>
              <LaunchPanelSubtitle>
                Everything you need to get discovered, build trust, and turn interest into booked work.
              </LaunchPanelSubtitle>
            </LaunchPanelHeader>
            <LaunchPanelBody sx={{ display: 'flex', flexDirection: 'column' }}>
              <AboutInnerWell
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100%',
                }}
              >
                <AboutPointsStack sx={{ flex: 3, justifyContent: 'space-between' }}>
                  {providerBenefits.map((benefit) => (
                    <AboutPointCard key={benefit.title}>
                      <AboutPointMark aria-hidden>{benefit.mark}</AboutPointMark>
                      <AboutPointText>
                        <AboutPointTitle>{benefit.title}</AboutPointTitle>
                        <AboutPointDescription>{benefit.description}</AboutPointDescription>
                      </AboutPointText>
                    </AboutPointCard>
                  ))}
                </AboutPointsStack>
                <Box sx={{ flex: 1 }} />
              </AboutInnerWell>
            </LaunchPanelBody>
          </LaunchPanel>
        </Grid>

        <Grid item xs={12}>
          <RolloutBanner>{rolloutMessage}</RolloutBanner>
        </Grid>
      </Grid>
      </Box>

      <LaunchPanel elevation={0} sx={{ mt: 3 }}>
        <LaunchPanelHeader>
          <LaunchPanelTitle component="h2" variant="h6">
            Business contact
          </LaunchPanelTitle>
        </LaunchPanelHeader>
        <LaunchPanelBody>
          <Typography variant="body2" sx={{ color: '#5a6478', mb: 2, lineHeight: 1.55 }}>
            Interested in being featured as a provider at launch? Send us a note and
            our team will follow up.
          </Typography>
          <ContactUsForm embedded />
          <ContactSignupSection>
            <ContactSignupDividerRow>
              <ContactSignupDividerLine />
              <ContactSignupDividerLabel component="span">Or</ContactSignupDividerLabel>
              <ContactSignupDividerLine />
            </ContactSignupDividerRow>
            <ContactSignupSubtitle>
              Ready to enroll? Create your business profile in a few minutes.
            </ContactSignupSubtitle>
            <ContactSignupButton
              component={RouterLink}
              to="/provider-signup"
              variant="contained"
              disableElevation
            >
              Sign Up Now
            </ContactSignupButton>
          </ContactSignupSection>
        </LaunchPanelBody>
      </LaunchPanel>
    </LaunchWrapper>
  );
}
